import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import AuthContext from "../../context/AuthContext";
import { apiGet, apiPost } from "../../Utils/apiFetch";
import { apiPath } from "../../Utils/apiPath";
import { levelMap, toFixedWithoutRounding } from "../../Utils/Constant";
import { useContextSelector } from "use-context-selector";
import Pagination from "../../components/Pagination";

let style = { padding: "8px" };

const DEFAULT_SEARCH_PARAMS = (createdBy) => ({
  page: 1,
  limit: 30,
  keyword: "",
  status: "active",
  created_by: createdBy,
});

const EMPTY_TOTALS = {
  Balance: 0,
  Available: 0,
  Exposure: 0,
  Credit_Reference: 0,
  Reference: 0,
};

const Banking = () => {
  const user = useContextSelector(AuthContext, (ctx) => {
    return ctx.user;
  });
  const getCoins = useContextSelector(AuthContext, (ctx) => {
    return ctx.getCoins;
  });

  const user_coins = useContextSelector(AuthContext, (ctx) => {
    return ctx.user_coins;
  });
  const createdBy = user?.id;

  const [searchParams, setSearchParams] = useState(() =>
    DEFAULT_SEARCH_PARAMS(createdBy),
  );
  const [searchInput, setSearchInput] = useState("");
  const [results, setResults] = useState({});
  const [creditLimit, setCreditLimit] = useState({});
  const [totalTable, setTotalTable] = useState(EMPTY_TOTALS);
  const [rowForm, setRowForm] = useState({});
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });
  const getRow = (id) => rowForm[id] || { txnType: "", amount: "", remark: "" };

  const updateRow = (id, patch) => {
    setRowForm((prev) => ({
      ...prev,
      [id]: { ...getRow(id), ...patch },
    }));
  };

  const handlePageChange = (newPage) => {
    setSearchParams((prev) => ({ ...prev, page: newPage }));
  };

  const getCreditLimit = async () => {
    const { status, data: response } = await apiGet(apiPath.checkLimit);
    if (status === 200 && response.success) {
      setCreditLimit(response.results);
    }
  };

  // Used to ignore stale responses when a newer request has started
  const requestId = useRef(0);

  const getUsers = async (paramsOverride) => {
    const params = paramsOverride || searchParams;
    const current = ++requestId.current;

    const { status, data: response } = await apiGet(
      apiPath.profileList,
      params,
    );

    // ignore the response if a newer request has started
    if (current !== requestId.current) return;

    if (status === 200 && response.success) {
      const list = response?.results;
      setResults(list);

      const rows = list?.data || [];
      if (rows.length > 0) {
        const totals = rows.reduce(
          (acc, item) => {
            acc.Balance += item.totalCoins || 0;
            acc.Available += item.totalCoins1 || 0;
            acc.Exposure += item.exposure || 0;
            acc.Credit_Reference += item.creditReference || 0;
            acc.Reference +=
              (item.totalCoins || 0) - (item.creditReference || 0);
            return acc;
          },
          {
            Balance: 0,
            Available: 0,
            Exposure: 0,
            Credit_Reference: 0,
            Reference: 0,
          },
        );
        setTotalTable(totals);
      } else {
        setTotalTable(EMPTY_TOTALS);
      }
    }
  };

  const refreshList = () => {
    getUsers();
    getCreditLimit();
  };

  // 1) Single source of truth for fetching the list
  useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.page, searchParams.status, searchParams.keyword]);

  // 2) Credit limit only needs to load once
  useEffect(() => {
    getCreditLimit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 3) Debounce typing, then update keyword and reset page in ONE state update
  useEffect(() => {
    const t = setTimeout(() => {
      const value = searchInput;
      if (value.length > 0 && value.length <= 2) return; // same rule as before
      setSearchParams((prev) =>
        prev.keyword === value ? prev : { ...prev, keyword: value, page: 1 },
      );
    }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const handleKeywordChange = (e) => {
    setSearchInput(e.target.value.replace(/\s+$/, ""));
  };

  const handleStatusChange = (e) => {
    const status = e.target.value;
    setSearchParams((prev) => ({ ...prev, status, page: 1 }));
  };

  const handleClearAll = () => {
    setRowForm({});
    setPassword("");
  };

  const handleFull = (item) => {
    const id = item.id ?? item._id;
    const row = getRow(id);
    if (row.txnType !== "2") return; // "Full" only applies to withdrawals
    updateRow(id, { amount: String(item.totalCoins1 ?? 0) });
  };

  const submitCount = useMemo(
    () => Object.values(rowForm).filter((r) => r.amount && r.txnType).length,
    [rowForm],
  );

  const handleSubmit = async () => {
    if (!password) {
      setMessage({ type: "error", text: "Please enter your password" });
      return;
    }

    const entries = Object.entries(rowForm).filter(
      ([, v]) => v.amount && v.txnType,
    );
    if (entries.length === 0) {
      setMessage({
        type: "error",
        text: "Enter an amount for at least one member",
      });
      return;
    }

    const transactionInsert = entries.map(([id, v]) => {
      const item = (results?.data || []).find(
        (r) => String(r.id ?? r._id) === String(id),
      );
      return {
        user_id: id,
        dw_type: v.txnType === "1" ? "D" : "W",
        amount: parseFloat(v.amount),
        remarks: v.remark || null,
        creditReference: parseFloat(item?.creditReference || 0),
      };
    });

    setIsSubmitting(true);
    try {
      const { status, data: response } = await apiPost(apiPath.bankingCreate, {
        password,
        transactionInsert,
      });
      if (status === 200 && response.success) {
        setMessage({ type: "success", text: response.message });
        getCoins();
        handleClearAll();
        refreshList();
        setSearchInput("");
      } else {
        setMessage({ type: "error", text: response.message });
      }
    } catch (err) {
      setMessage({
        type: "error",
        text: err?.response?.data?.message || "Something went wrong.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    if (!message.text) return;

    const timer = setTimeout(() => {
      setMessage({ type: "", text: "" });
    }, 3000);

    return () => clearTimeout(timer); // cleanup if a new message replaces this one, or component unmounts
  }, [message.text]);

  const rows = results?.data || [];
  return (
    <div _ngcontent-serverapp-c45 className="content-wrapper">
      <router-outlet _ngcontent-serverapp-c45 />
      <app-banking _nghost-serverapp-c87>
        <div _ngcontent-serverapp-c87 className="full-wrap">
          <h2 _ngcontent-serverapp-c87>Banking</h2>

          <div
            style={{ position: "relative" }}
            _ngcontent-serverapp-c87
            className="total_all"
          >
            {message.text && (
              <div
                _ngcontent-serverapp-c117
                className={`message-wrap ${message.type}`}
                id="message-6308766"
                style={{ position: "absolute", width: "100%" }}
              >
                <a
                  _ngcontent-serverapp-c117
                  className="btn-close"
                  onClick={() => setMessage({ type: "", text: "" })}
                >
                  Close
                </a>

                <p _ngcontent-serverapp-c117>{message.text}</p>
              </div>
            )}
            <div
              _ngcontent-serverapp-c87
              className="input-list premiun-inputlist"
              style={{ margin: "8px 0" }}
            >
              <div
                _ngcontent-serverapp-c87
                id="userSearchUl"
                className="search-wrap"
              >
                <input
                  _ngcontent-serverapp-c87
                  appinputrestriction="noSpecialChars"
                  type="text"
                  name="searchUserId"
                  id="searchUserId"
                  className="search-input"
                  placeholder="Enter userId..."
                  value={searchInput}
                  onChange={handleKeywordChange}
                />
                <button
                  _ngcontent-serverapp-c87
                  id="searchUserId"
                  className="search-but"
                  type="button"
                  onClick={() => getUsers()}
                >
                  {" "}
                  Search{" "}
                </button>
              </div>
              <ul
                _ngcontent-serverapp-c87
                id="accountStatusUl"
                className="input-list"
              >
                <li _ngcontent-serverapp-c87>
                  <strong _ngcontent-serverapp-c87>Status</strong>
                </li>
                <li _ngcontent-serverapp-c87>
                  <select
                    _ngcontent-serverapp-c87
                    name="accountStatus"
                    id="accountStatus"
                    value={searchParams?.status}
                    onChange={handleStatusChange}
                  >
                    <option _ngcontent-serverapp-c87 value="">
                      All{" "}
                    </option>
                    <option _ngcontent-serverapp-c87 value="active">
                      Active{" "}
                    </option>
                    <option _ngcontent-serverapp-c87 value="locked">
                      Locked{" "}
                    </option>
                    <option _ngcontent-serverapp-c87 value="suspend">
                      Suspended{" "}
                    </option>
                  </select>
                </li>
              </ul>
              <div _ngcontent-serverapp-c87 className="flex-1" />
            </div>
            <div _ngcontent-serverapp-c87 className="total_alll">
              <button
                _ngcontent-serverapp-c87
                id="refresh"
                className="btn_replay btn"
                type="button"
                onClick={refreshList}
              >
                <img
                  _ngcontent-serverapp-c87
                  src="/assets/images/transparent.gif"
                />
              </button>
            </div>
          </div>

          <div _ngcontent-serverapp-c87 className="function-wrap clearfix">
            <dl _ngcontent-serverapp-c87 className="banking-head float-L">
              <dt _ngcontent-serverapp-c87>Your Balance </dt>
              <dd
                style={{ marginBottom: "0" }}
                _ngcontent-serverapp-c87
                id="yourBalance"
              >
                <span _ngcontent-serverapp-c87> USD</span>{" "}
                {toFixedWithoutRounding(user_coins)}
              </dd>
            </dl>
            <ul
              _ngcontent-serverapp-c87
              className="input-list float-R"
              style={{ display: "none" }}
            >
              <li _ngcontent-serverapp-c87>
                <label _ngcontent-serverapp-c87>Sort by </label>
              </li>
              <li _ngcontent-serverapp-c87>
                <select _ngcontent-serverapp-c87 id="sort">
                  <option _ngcontent-serverapp-c87 value="userId">
                    UID{" "}
                  </option>
                  <option _ngcontent-serverapp-c87 value="site">
                    Site{" "}
                  </option>
                  <option _ngcontent-serverapp-c87 value="currencyType">
                    Currency{" "}
                  </option>
                </select>
              </li>
            </ul>
          </div>

          <div
            _ngcontent-serverapp-c87
            id="totalBox"
            className="total_box filtertotaldivmob"
          >
            <dl
              _ngcontent-serverapp-c87
              id="creditLimitDL"
              className="total_dl_mob"
            >
              <dt _ngcontent-serverapp-c87>Credit Reference</dt>
              <dd _ngcontent-serverapp-c87 id="myCreditLimit" className="push">
                <strong _ngcontent-serverapp-c87>
                  {" "}
                  {toFixedWithoutRounding(
                    creditLimit?.creditReference ??
                      creditLimit?.creditLimit ??
                      0,
                  )}
                </strong>{" "}
                (USD)
              </dd>
            </dl>

            <div _ngcontent-serverapp-c87 className="text-center">
              <span _ngcontent-serverapp-c87 id="user">
                <i _ngcontent-serverapp-c87 className="fa fa-chevron-down" />
              </span>
            </div>
          </div>

          <div _ngcontent-serverapp-c87>
            <form
              _ngcontent-serverapp-c87
              noValidate
              onSubmit={(e) => e.preventDefault()}
            >
              <div
                _ngcontent-serverapp-c87
                className="over-wrap"
                style={{ maxHeight: "calc(100% - 108px)" }}
              >
                <ul _ngcontent-serverapp-c87 className="report-tab-wrap box">
                  <li
                    _ngcontent-serverapp-c87
                    id="reportType_sportsBook"
                    className="report-tab select"
                  >
                    {" "}
                    Credit Reference{" "}
                  </li>
                </ul>

                <div
                  _ngcontent-serverapp-c87
                  className="table-responsive data-table"
                >
                  <table
                    _ngcontent-serverapp-c87
                    id="table_transfer"
                    className="table01 tab-transfer tab-banking"
                  >
                    <tbody _ngcontent-serverapp-c87>
                      <tr _ngcontent-serverapp-c87>
                        <th
                          style={style}
                          _ngcontent-serverapp-c87
                          id="userIdTH"
                          width="15%"
                          data-name="userName"
                          className="align-L"
                        >
                          UID
                        </th>
                        <th
                          _ngcontent-serverapp-c87
                          id="siteTH"
                          width="5%"
                          style={{ display: "none" }}
                        >
                          Site
                        </th>
                        <th
                          _ngcontent-serverapp-c87
                          id="currencyTypeTH"
                          width="5%"
                          style={{ display: "none" }}
                        >
                          {" "}
                          Currency{" "}
                        </th>
                        <th
                          style={style}
                          _ngcontent-serverapp-c87
                          width="11%"
                          data-name="availableBalance"
                        >
                          Available Balance
                        </th>
                        <th
                          style={style}
                          _ngcontent-serverapp-c87
                          width="11%"
                          data-name="downlineBalance"
                        >
                          {" "}
                          Balance in Downline{" "}
                        </th>

                        <th
                          style={style}
                          _ngcontent-serverapp-c87
                          width="11%"
                          data-name="exposure"
                        >
                          {" "}
                          Exposure in Downline
                        </th>
                        <th
                          style={style}
                          _ngcontent-serverapp-c87
                          width="20%"
                          className="align-C border-l"
                        >
                          Deposit / Withdraw
                        </th>
                        <th
                          style={style}
                          _ngcontent-serverapp-c87
                          width="11%"
                          className="border-l"
                        >
                          Credit Reference
                        </th>
                        <th
                          style={style}
                          _ngcontent-serverapp-c87
                          width="10%"
                          data-name="refPL"
                        >
                          Reference P/L
                        </th>

                        <th
                          style={style}
                          _ngcontent-serverapp-c87
                          width={10}
                          className="border-l"
                        >
                          Remark
                        </th>
                        <th
                          style={style}
                          _ngcontent-serverapp-c87
                          width={47}
                          className="full-amount"
                        >
                          <Link
                            _ngcontent-serverapp-c87
                            id="allLog"
                            className="btn-send"
                            to="/banking/bankinglog"
                          >
                            All
                          </Link>
                        </th>
                      </tr>
                    </tbody>
                    <tbody _ngcontent-serverapp-c87 id="content">
                      {rows.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="align-C">
                            No members found.
                          </td>
                        </tr>
                      ) : (
                        rows.map((item, index) => {
                          const id = item.id ?? item._id;
                          const row = getRow(id);
                          const availableBalance = item.totalCoins1 ?? 0;
                          const downlineBalance = item.totalCoins ?? 0;
                          const exposure = item.exposure ?? 0;
                          const creditReference = item.creditReference ?? 0;
                          const referencePL =
                            item.referencePL ??
                            downlineBalance - creditReference;

                          return (
                            <tr
                              key={id}
                              className="ng-untouched ng-pristine ng-valid ng-star-inserted"
                            >
                              <td className="td-uid ng-star-inserted">
                                <span className="order">
                                  {(searchParams.page - 1) * searchParams.limit +
                                    index +
                                    1}.
                                </span>

                                <span
                                  className={`Bdlevel ${levelMap[item?.userType]} premium`}
                                ></span>

                                {item.username}
                              </td>

                              <td className="ng-star-inserted">
                                {toFixedWithoutRounding(availableBalance)}
                              </td>

                              <td
                                id="balanceDownline"
                                className="ng-star-inserted"
                              >
                                {toFixedWithoutRounding(downlineBalance)}
                              </td>

                              <td
                                id="userExposure"
                                className="ng-star-inserted"
                              >
                                <span className="red">
                                  ({toFixedWithoutRounding(exposure)})
                                </span>
                              </td>

                              <td className="DW-amount">
                                <div className="displayfelx">
                                  <input
                                    type="radio"
                                    name={`txnType_${id}`}
                                    value="1"
                                    checked={row.txnType === "1"}
                                    onChange={(e) =>
                                      updateRow(id, { txnType: e.target.value })
                                    }
                                    style={{ display: "none" }}
                                  />

                                  <input
                                    type="radio"
                                    name={`txnType_${id}`}
                                    value="2"
                                    checked={row.txnType === "2"}
                                    onChange={(e) =>
                                      updateRow(id, { txnType: e.target.value })
                                    }
                                    style={{ display: "none" }}
                                  />

                                  <ul
                                    className={`btn_list-DW ${
                                      row.txnType === "2" ? "select-W" : ""
                                    } ${row.txnType === "1" ? "select-D" : ""}`}
                                  >
                                    <li>
                                      <button
                                        type="button"
                                        className={`btn ${
                                          row.txnType === "1" ? "active" : ""
                                        }`}
                                        onClick={() =>
                                          updateRow(id, { txnType: "1" })
                                        }
                                      >
                                        D
                                      </button>
                                    </li>

                                    <li>
                                      <button
                                        type="button"
                                        className={`btn ${
                                          row.txnType === "2" ? "active" : ""
                                        }`}
                                        onClick={() =>
                                          updateRow(id, { txnType: "2" })
                                        }
                                      >
                                        W
                                      </button>
                                    </li>
                                  </ul>

                                  {row.txnType === "2" && (
                                    <span
                                      className="minus"
                                      style={{
                                        position: "absolute",
                                        fontWeight: "700",
                                        left: "75px",
                                        lineHeight: "29px",
                                        fontSize: "13px",
                                        color: "#d0021b",
                                      }}
                                    >
                                      -
                                    </span>
                                  )}
                                  {row.txnType === "1" && (
                                    <span
                                      className="plus"
                                      style={{
                                        position: "absolute",
                                        fontWeight: "700",
                                        left: "75px",
                                        lineHeight: "29px",
                                        fontSize: "13px",
                                        color: "rgb(56, 179, 56)",
                                      }}
                                    >
                                      +
                                    </span>
                                  )}
                                  <input
                                    name={`amount_${id}`}
                                    type="number"
                                    placeholder="0"
                                    value={row.amount}
                                    onChange={(e) =>
                                      updateRow(id, { amount: e.target.value })
                                    }
                                  />

                                  <button
                                    type="button"
                                    className={`btn float-L ${
                                      row.txnType === "2" ? "" : "disable"
                                    } `}
                                    disabled={row.txnType !== "2"}
                                    onClick={() => handleFull(item)}
                                  >
                                    Full
                                  </button>
                                </div>
                              </td>

                              <td className="credit-amount ng-star-inserted">
                                <div>
                                  <span>
                                    {toFixedWithoutRounding(creditReference)}
                                  </span>
                                </div>
                              </td>

                              <td
                                id="userReferencePL"
                                className="ng-star-inserted"
                              >
                                <span
                                  className={
                                    referencePL > 0
                                      ? ""
                                      : "red ng-star-inserted"
                                  }
                                >
                                  {Math.abs(
                                    toFixedWithoutRounding(referencePL),
                                  )}
                                </span>
                              </td>

                              <td className="border-l ng-star-inserted">
                                <input
                                  name={`remark_${id}`}
                                  type="text"
                                  placeholder="Remark"
                                  value={row.remark}
                                  onChange={(e) =>
                                    updateRow(id, { remark: e.target.value })
                                  }
                                />
                              </td>

                              <td className="full-amount ng-star-inserted">
                                <Link
                                  id="log"
                                  className="btn"
                                  to={`/banking/bankinglog/${id}`}
                                >
                                  Log
                                </Link>
                              </td>
                            </tr>
                          );
                        })
                      )}

                      <tr
                        _ngcontent-serverapp-c87
                        id="totalTempTr"
                        className="total"
                      >
                        <td _ngcontent-serverapp-c87 className="align-L">
                          Total
                        </td>
                        <td _ngcontent-serverapp-c87 id="totalAvailableBalance">
                          {" "}
                          {toFixedWithoutRounding(totalTable.Available)}{" "}
                        </td>
                        <td _ngcontent-serverapp-c87 id="totalBalanceDownline">
                          {" "}
                          {toFixedWithoutRounding(totalTable.Balance)}{" "}
                        </td>

                        <td _ngcontent-serverapp-c87 id="totalExposure">
                          <span _ngcontent-serverapp-c87 className="red">
                            ({toFixedWithoutRounding(totalTable.Exposure)})
                          </span>
                        </td>
                        <td
                          _ngcontent-serverapp-c87
                          id="totalCreditReference"
                          colSpan={2}
                        >
                          {" "}
                          {toFixedWithoutRounding(
                            totalTable.Credit_Reference,
                          )}{" "}
                        </td>
                        <td _ngcontent-serverapp-c87 id="totalReferencePL">
                          <span
                            className={
                              totalTable.Reference > 0
                                ? ""
                                : "red ng-star-inserted"
                            }
                            _ngcontent-serverapp-c87
                          >
                            {Math.abs(
                              toFixedWithoutRounding(totalTable.Reference),
                            )}
                          </span>
                        </td>
                        <td _ngcontent-serverapp-c87 />
                        <td _ngcontent-serverapp-c87 />
                      </tr>
                    </tbody>
                  </table>
                </div>

              
              </div>
              <div
                _ngcontent-serverapp-c87
                id="settlementBar"
                className="submit-wrap"
              >
                <ul _ngcontent-serverapp-c87>
                  <li _ngcontent-serverapp-c87>
                    <a
                      _ngcontent-serverapp-c87
                      id="clearAllBtn"
                      className="btn"
                      onClick={handleClearAll}
                    >
                      Clear All
                    </a>
                  </li>
                  <li _ngcontent-serverapp-c87 className="submit-payment">
                    <input
                      _ngcontent-serverapp-c87
                      formcontrolname="password"
                      id="paymentPassword"
                      type="password"
                      autoComplete="new-password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <button
                      _ngcontent-serverapp-c87
                      id="submit"
                      className="btn-send"
                      type="button"
                      onClick={handleSubmit}
                      disabled={submitCount === 0 || isSubmitting}
                    >
                      {" "}
                      {isSubmitting ? "Submitting..." : "Submit"}{" "}
                      <span _ngcontent-serverapp-c87 id="submitCount">
                        {submitCount}{" "}
                      </span>{" "}
                      Payment{" "}
                    </button>
                  </li>
                </ul>
              </div>


                {results?.data?.length > 0 && results?.totalDocs > 0 && (
                  <Pagination
                    pagination={results}
                    onPageChange={handlePageChange}
                  />
                )}
            </form>
          </div>
        </div>
      </app-banking>
    </div>
  );
};

export default Banking;
